/* iter-training-v-ach-coach — Achievement primitives.
 *
 * Small idempotent helper around POST /api/authors/me/achievements/{key}.
 * Caches awarded keys in localStorage so callers can call `award(key)`
 * as often as they like — a second call for the same key is a no-op.
 * Fires a `pw-achievement-awarded` window event on a genuine award so
 * downstream UIs (bookshelf badges, toasts) can react without polling.
 */
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const LS_KEY = 'pw_achievements_v1';

function _load() {
  try { return JSON.parse(window.localStorage.getItem(LS_KEY) || '{}'); }
  catch { return {}; }
}
function _save(obj) {
  try { window.localStorage.setItem(LS_KEY, JSON.stringify(obj)); }
  catch { /* ignore */ }
}

export function getLocalAwarded() {
  return _load();
}

export async function refreshFromServer() {
  try {
    const r = await axios.get(`${API}/authors/me/achievements`);
    const map = {};
    for (const row of r.data?.achievements || []) map[row.key] = row.awarded_at;
    _save(map);
    return { map, catalog: r.data?.catalog || {} };
  } catch {
    return { map: _load(), catalog: {} };
  }
}

export async function award(key) {
  if (!key) return { awarded: false };
  const cache = _load();
  if (cache[key]) return { awarded: false, cached: true };
  try {
    const r = await axios.post(
      `${API}/authors/me/achievements/${encodeURIComponent(key)}`,
    );
    const ts = r?.data?.achievement?.awarded_at || new Date().toISOString();
    cache[key] = ts;
    _save(cache);
    if (r?.data?.awarded) {
      try {
        window.dispatchEvent(new CustomEvent('pw-achievement-awarded', {
          detail: { key, meta: r.data.meta, achievement: r.data.achievement },
        }));
      } catch { /* ignore */ }
    }
    return r.data || { awarded: false };
  } catch {
    return { awarded: false, error: true };
  }
}

export function hasAwarded(key) {
  return !!_load()[key];
}
